import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ClarityModule } from '@clr/angular';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  speakerClass,
  speakerLabel,
  speakerSequence,
  speakerTitle,
  splitTurns,
  VoxtralSpeaker,
} from '../voxtral-speaker';

/**
 * Presentational Voxtral live-data panel — extracted verbatim from
 * DetailsComponent (system messages, Aktuell partial, Transkript, the
 * correction rings, the Korrektur-API snapshot and the Segmente-Zeitachse).
 *
 * IMPORTANT — change detection contract: every array/object input is a
 * PARENT-OWNED, IN-PLACE-MUTATED structure with stable identity (unshift/pop
 * rings, memoized filtered list, snapshot object). This component must stay
 * on DEFAULT change detection and must never clone or go OnPush — it renders
 * whatever the parent's WS/poll handlers mutated on the shared references.
 * Input names deliberately match the former parent fields so the moved
 * template is byte-identical.
 */
@Component({
  selector: 'app-voxtral-live-data',
  standalone: true,
  imports: [CommonModule, FormsModule, ClarityModule],
  templateUrl: './voxtral-live-data.component.html',
  styleUrl: './voxtral-live-data.component.scss',
})
export class VoxtralLiveDataComponent {
  @Input() voxtralSystemMessages: any[] = [];
  @Input() voxtralCurrentText = '';
  @Input() voxtralCurrentHtml: SafeHtml | string = '';
  @Input() voxtralTranscriptLines: string[] = [];
  @Input() voxtralTranscriptHtml: (SafeHtml | string)[] = [];
  // 2026-09-28 — live speaker diarization; Speakers runs parallel to Html.
  @Input() voxtralCurrentSpeaker: VoxtralSpeaker | null = null;
  @Input() voxtralTranscriptSpeakers: (VoxtralSpeaker | null)[] = [];
  @Input() voxtralRecentCorrections: any[] = [];
  @Input() voxtralRecentRejections: any[] = [];
  @Input() voxtralSessionApiBase: string | null = null;
  @Input() voxtralCorrectionApiSnapshot: any = { applied: [], rejected: [] };
  @Input() voxtralSegmentTimeline: any[] = [];
  @Input() voxtralFilteredSegments: any[] = [];
  @Input() voxtralSegmentScopeFilter: 'all' | 'final' | 'partial' = 'all';
  @Output() voxtralSegmentScopeFilterChange = new EventEmitter<'all' | 'final' | 'partial'>();
  @Output() blocklistAdd = new EventEmitter<string>();

  // Pure UI disclosure state (was parent state; resets when the hosting
  // accordion panel unmounts — acceptable, mirrors a fresh dialog open).
  voxtralRecentCorrectionsExpanded = false;
  voxtralRecentRejectionsExpanded = false;
  voxtralCorrectionApiExpanded = false;
  voxtralSegmentTimelineExpanded = false;

  constructor(private sanitizer: DomSanitizer) {}

  onScopeFilterChange(v: 'all' | 'final' | 'partial') {
    this.voxtralSegmentScopeFilter = v;
    this.voxtralSegmentScopeFilterChange.emit(v);
  }

  /** Emit wrappers keep the moved template's call sites verbatim. */
  addToBlocklist(word: string): void {
    this.blocklistAdd.emit(word);
  }

  /** Total entries currently in the API-panel snapshot — drives the header count. */
  get voxtralCorrectionApiTotal(): number {
    const s = this.voxtralCorrectionApiSnapshot;
    return (s.applied?.length || 0) + (s.rejected?.length || 0);
  }

  /** trackBy for the per-segment timeline *ngFor — keyed by stable seq. */
  trackBySegmentSeq(_i: number, s: { seq: number }): number {
    return s.seq;
  }

  /** trackBy for the correction/rejection rings — keyed by timestamp. */
  trackByCorrectionTs(i: number, c: { ts: number }): string {
    return `${c.ts}|${i}`;
  }

  // Speaker chip helpers (see ../voxtral-speaker.ts).
  readonly speakerSequence = speakerSequence;
  readonly speakerLabel = speakerLabel;
  readonly speakerTitle = speakerTitle;
  readonly speakerClass = speakerClass;
  readonly splitTurns = splitTurns;

  /** Colour class for a timeline row's left border — its dominant speaker. */
  rowSpeakerClass(s: { speaker?: VoxtralSpeaker | null }): string {
    const dominant = s.speaker?.speaker;
    return dominant != null ? `voxtral-segment-spk ${speakerClass(dominant)}` : '';
  }

  /** Human label for a correction stage chip in the admin corrections view. */
  correctionStageLabel(stage: string | undefined | null): string {
    switch ((stage || 'fuzzy').toLowerCase()) {
      case 'phonetic': return 'Phonetisch';
      case 'llm': return 'LLM';
      case 'hybrid': return 'Hybrid';
      case 'fast_hybrid': return 'Fast-Hybrid';
      default: return 'Fuzzy';
    }
  }

  /**
   * Render an applied correction as its FULL segment with the replaced part
   * shown inline: the original word struck-through (red) immediately followed
   * by the corrected word (green). Falls back to a bare word-level `orig→corr`
   * diff when the segment context is missing (legacy Stage-1 rows) or when the
   * corrected word can't be located in the segment.
   */
  correctionSegmentHtml(c: any): SafeHtml {
    const escape = (s: string) => (s || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const orig = (c && c.original) || '';
    const corr = (c && c.corrected) || '';
    const seg = (c && c.corrected_segment) || '';
    const diff =
      `<del class="voxtral-seg-del">${escape(orig)}</del>` +
      `<ins class="voxtral-seg-ins">${escape(corr)}</ins>`;
    // Splice the del/ins pair in at the corrected word's position so the
    // operator reads the whole sentence with the fix highlighted in place.
    // No segment context (legacy row) or word not found → bare word-level diff.
    const idx = seg && corr ? seg.indexOf(corr) : -1;
    if (idx < 0) {
      return this.sanitizer.bypassSecurityTrustHtml(diff);
    }
    const before = escape(seg.slice(0, idx));
    const after = escape(seg.slice(idx + corr.length));
    return this.sanitizer.bypassSecurityTrustHtml(before + diff + after);
  }
}
